import React from "react";
import { Link } from "react-router-dom";
import SEOMeta from "../components/SEOMeta";

const PrivacyPolicy: React.FC = () => {
  // Structured data for SEO
  const structuredData = {
    "@context": "https://schema.org",
    "@type": "WebPage",
    name: "Privacy Policy",
    description: "Privacy Policy for CyberAnnals - Learn about our data collection, use of cookies, and how we protect your privacy when using our cybersecurity archive website.",
    url: "https://cyberannals.com/privacy-policy",
    about: {
      "@type": "Organization",
      name: "CyberAnnals",
      description: "Comprehensive historical archive of cyber attacks dedicated to documenting and preserving the chronicles of cyber attacks throughout history."
    }
  };

  return (
    <>
      <SEOMeta
        title="Privacy Policy - CyberAnnals | Cybersecurity Archive"
        description="Privacy Policy for CyberAnnals - Learn about our data collection, use of cookies, and how we protect your privacy when using our educational cybersecurity archive website."
        keywords="privacy policy, cyberannals privacy, data collection, cookies policy, personal information, privacy protection, cybersecurity website privacy"
        canonical="https://cyberannals.com/privacy-policy"
        author="Muhammad Hussain"
        ogType="website"
        ogImage="https://cyberannals.com/cyberAnnals_logo.png"
        structuredData={structuredData}
      />

      {/* Hidden JSON-LD for structured data */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(structuredData) }}
      />

      <div className="min-h-screen bg-white dark:bg-slate-950 text-slate-900 dark:text-slate-100 py-12">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Header */}
          <div className="text-center mb-16">
            <h1 className="text-4xl md:text-5xl font-bold mb-6 bg-gradient-to-r from-blue-600 to-blue-400 dark:from-blue-400 dark:to-blue-300 bg-clip-text text-transparent">
              Privacy Policy
            </h1>
            <div className="w-24 h-1 bg-gradient-to-r from-blue-500 to-amber-500 mx-auto rounded-full"></div>
            <p className="text-lg text-slate-600 dark:text-slate-400 mt-6 max-w-2xl mx-auto">
              Last updated: {new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}
            </p>
          </div>

          <div className="prose prose-lg dark:prose-invert max-w-none">
            <section className="mb-12">
              <h2 className="text-2xl md:text-3xl font-bold mb-6 text-blue-600 dark:text-blue-400">
                Introduction
              </h2>
              <p className="text-lg mb-4 leading-relaxed">
                Welcome to CyberAnnals, an educational cybersecurity archive website. We respect your privacy and are committed to protecting your personal data. This privacy policy explains how we collect, use, and protect your information when you visit our educational content website.
              </p>
              <p className="text-lg leading-relaxed">
                This policy is effective as of the date listed above and applies to all information collected through our educational website at https://cyberannals.com.
              </p>
            </section>

            <section className="mb-12">
              <h2 className="text-2xl md:text-3xl font-bold mb-6 text-blue-600 dark:text-blue-400">
                Information We Collect
              </h2>
              <h3 className="text-xl font-semibold mb-4 text-slate-800 dark:text-slate-200">
                Personal Information
              </h3>
              <p className="text-lg mb-4 leading-relaxed">
                When you use our contact form, we may collect:
              </p>
              <ul className="list-disc pl-6 space-y-2 mb-6">
                <li>Your name</li>
                <li>Email address</li>
                <li>Subject of your inquiry</li>
                <li>Message content</li>
              </ul>

              <h3 className="text-xl font-semibold mb-4 text-slate-800 dark:text-slate-200">
                Automatically Collected Information
              </h3>
              <p className="text-lg mb-4 leading-relaxed">
                We may automatically collect certain information about your device and usage of our website:
              </p>
              <ul className="list-disc pl-6 space-y-2 mb-6">
                <li>IP address</li>
                <li>Browser type and version</li>
                <li>Operating system</li>
                <li>Pages visited and time spent on pages</li>
                <li>Referring website</li>
                <li>Date and time of access</li>
              </ul>
            </section>

            <section className="mb-12">
              <h2 className="text-2xl md:text-3xl font-bold mb-6 text-blue-600 dark:text-blue-400">
                How We Use Your Information
              </h2>
              <p className="text-lg mb-4 leading-relaxed">
                We use the information we collect in the following ways:
              </p>
              <ul className="list-disc pl-6 space-y-2 mb-6">
                <li>To respond to your inquiries about our educational content</li>
                <li>To improve our educational website and user experience</li>
                <li>To analyze website usage and enhance our educational content</li>
                <li>To comply with legal obligations</li>
                <li>To protect our legal rights</li>
              </ul>
            </section>

            <section className="mb-12">
              <h2 className="text-2xl md:text-3xl font-bold mb-6 text-blue-600 dark:text-blue-400">
                Cookies and Tracking Technologies
              </h2>
              <p className="text-lg mb-4 leading-relaxed">
                We use cookies and similar tracking technologies to enhance your experience on our website. Cookies are small data files stored on your device that help us:
              </p>
              <ul className="list-disc pl-6 space-y-2 mb-6">
                <li>Remember your preferences</li>
                <li>Analyze website traffic and usage</li>
                <li>Deliver personalized content</li>
                <li>Provide and improve our services</li>
              </ul>
              <p className="text-lg leading-relaxed">
                We partner with third-party advertisers, including Google AdSense, which may use cookies to serve personalized advertisements based on your visits to our site and other sites on the internet.
              </p>
            </section>

            <section className="mb-12">
              <h2 className="text-2xl md:text-3xl font-bold mb-6 text-blue-600 dark:text-blue-400">
                Google AdSense and Advertising
              </h2>
              <p className="text-lg mb-4 leading-relaxed">
                Our educational website uses Google AdSense to serve relevant advertisements that support our mission of providing free cybersecurity education. Google, as a third-party vendor, uses cookies to serve ads on our site. These cookies help deliver ads based on your visits to our educational content website and other sites on the internet. The ads help support the continued development and maintenance of our free educational resources.
              </p>
              <p className="text-lg mb-4 leading-relaxed">
                You may opt out of personalized advertising by visiting Google's Ads Settings or by using an ad blocker. Alternatively, you can opt out of a third-party vendor's use of cookies for personalized advertising by visiting www.aboutads.info.
              </p>
            </section>

            <section className="mb-12">
              <h2 className="text-2xl md:text-3xl font-bold mb-6 text-blue-600 dark:text-blue-400">
                Data Sharing and Disclosure
              </h2>
              <p className="text-lg mb-4 leading-relaxed">
                We do not sell, trade, or rent your personal information to third parties. We may share your information in the following circumstances:
              </p>
              <ul className="list-disc pl-6 space-y-2 mb-6">
                <li>With your consent</li>
                <li>To comply with legal obligations</li>
                <li>To protect and defend our rights and property</li>
                <li>To prevent or investigate possible wrongdoing in connection with our website</li>
                <li>To protect the personal safety of users of our website</li>
                <li>With trusted service providers who assist us in operating our website (such as hosting, analytics, and email services) under strict confidentiality agreements</li>
              </ul>
            </section>

            <section className="mb-12">
              <h2 className="text-2xl md:text-3xl font-bold mb-6 text-blue-600 dark:text-blue-400">
                Data Security
              </h2>
              <p className="text-lg mb-4 leading-relaxed">
                We implement appropriate security measures including encryption and secure transmission protocols to protect your personal information against unauthorized access, alteration, disclosure, or destruction. While we follow industry-standard practices to protect your information, please note that no method of transmission over the internet or method of electronic storage is 100% secure.
              </p>
            </section>

            <section className="mb-12">
              <h2 className="text-2xl md:text-3xl font-bold mb-6 text-blue-600 dark:text-blue-400">
                Your Rights
              </h2>
              <p className="text-lg mb-4 leading-relaxed">
                Depending on your location, you may have the following rights regarding your personal information:
              </p>
              <ul className="list-disc pl-6 space-y-2 mb-6">
                <li>The right to request deletion of your data</li>
                <li>The right to restrict processing of your data</li>
                <li>The right to data portability</li>
                <li>The right to object to processing</li>
              </ul>
              <p className="text-lg leading-relaxed">
                To exercise any of these rights, please contact us using the information provided below.
              </p>
            </section>

            <section className="mb-12">
              <h2 className="text-2xl md:text-3xl font-bold mb-6 text-blue-600 dark:text-blue-400">
                Children's Privacy
              </h2>
              <p className="text-lg mb-4 leading-relaxed">
                Our website does not address anyone under the age of 13. We do not knowingly collect personal information from children under 13. If you are a parent or guardian and you are aware that your child has provided us with personal information, please contact us.
              </p>
            </section>

            <section className="mb-12">
              <h2 className="text-2xl md:text-3xl font-bold mb-6 text-blue-600 dark:text-blue-400">
                Changes to This Privacy Policy
              </h2>
              <p className="text-lg mb-4 leading-relaxed">
                We may update our privacy policy from time to time. We will notify you of any changes by posting the new privacy policy on this page and updating the "Last updated" date.
              </p>
              <p className="text-lg leading-relaxed">
                You are advised to review this privacy policy periodically for any changes. Changes to this privacy policy are effective when they are posted on this page.
              </p>
            </section>

            <section className="mb-12">
              <h2 className="text-2xl md:text-3xl font-bold mb-6 text-blue-600 dark:text-blue-400">
                Contact Us
              </h2>
              <p className="text-lg mb-4 leading-relaxed">
                If you have any questions about this privacy policy, please contact us:
              </p>
              <div className="bg-slate-50 dark:bg-slate-900/50 rounded-xl p-6 border border-slate-200 dark:border-slate-700">
                <div className="space-y-4">
                  <div>
                    <p className="font-medium text-slate-700 dark:text-slate-300 mb-2">Contact Form:</p>
                    <p className="text-slate-800 dark:text-slate-200">
                      <a href="/contact" className="text-blue-600 hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300 underline">https://cyberannals.com/contact</a>
                    </p>
                  </div>
                </div>
              </div>
            </section>
          </div>

          {/* Navigation Links */}
          <div className="flex flex-wrap justify-center gap-4 pt-8 border-t border-slate-200 dark:border-slate-700 mt-12">
            <Link
              to="/terms-of-service"
              className="px-6 py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors font-medium text-sm"
            >
              Terms of Service
            </Link>
            <Link
              to="/about"
              className="px-6 py-3 bg-slate-200 hover:bg-slate-300 dark:bg-slate-700 dark:hover:bg-slate-600 text-slate-800 dark:text-slate-200 rounded-lg transition-colors font-medium text-sm"
            >
              About Us
            </Link>
            <Link
              to="/"
              className="px-6 py-3 bg-slate-200 hover:bg-slate-300 dark:bg-slate-700 dark:hover:bg-slate-600 text-slate-800 dark:text-slate-200 rounded-lg transition-colors font-medium text-sm"
            >
              Home
            </Link>
          </div>
        </div>
      </div>
    </>
  );
};

export default PrivacyPolicy;